import { inject, Injectable, SecurityContext } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom, map } from 'rxjs';
import { DomSanitizer } from '@angular/platform-browser';
import { AuthService } from './auth.service';

export interface ProfileI {
  id: string;
  title: string;
  imageUrl: string;
  description: string;
  completeName: string;
}

@Injectable({
  providedIn: 'root'
})
export class ProfilesService {
  private apiUrl = environment.apiUrl;
  private httpClient = inject(HttpClient);
  private authService = inject(AuthService);
  private sanitizer = inject(DomSanitizer);

  private headers = new HttpHeaders({
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
    'Expires': '0',
  });

  async getImportantProfilesFromApi(role: 'advisor' | 'supplier' | 'sponsor') {
    const timestamp = new Date().getTime();
    return firstValueFrom(
      this.httpClient.get<ProfileI[]>(`${this.apiUrl}/profiles?important=1&paginate=disabled&role=${role}&t=${timestamp}`, { headers: this.headers })
      .pipe(
        map((response: any) => {
          const profiles = response.data.slice(0, 4);
          // const totalPages = response.meta.last_page;
          // const currentPage = response.meta.current_page;
          // const total = response.meta.total;

          return {
            data: profiles.map((profile: any) => ({
              id: profile.id,
              title: profile.title,
              imageUrl: profile.image ? profile.image.url : '',
              description: profile.summary,
              completeName: this.stringCamelCase(
                profile.client.name +
                  ' ' +
                  profile.client.first_surname +
                  ' ' +
                  (profile.client.second_surname || ''),
              ),
              advisor_type: profile?.advisor_type,
            })),
            // totalPages,
            // currentPage,
            // total
          };
        })
    ));
  }

  async getPaginatedProfilesFromApi(role: 'advisor' | 'supplier' | 'sponsor', advisorType: string | null = null, page: number, pageSize: number = 6, query: string = '', tags: string[] = []) {
    
    const tagsQuery = tags.map(tag => `tags[]=${encodeURIComponent(tag)}`).join('&');
    
    return firstValueFrom(
      this.httpClient.get(`${this.apiUrl}/profiles?paginate=enabled&per_page=${pageSize}&page=${page}&search=${query}&role=${role}${advisorType ? '&advisor_type=' + advisorType : ''}${tags.length ? '&' + tagsQuery : ''}`)
        .pipe(
          map((response: any) => {
            const profiles = response.data;
            const totalPages = response.meta.last_page;
            const currentPage = response.meta.current_page;
            const totalPosts = response.meta.total;

            return {
              data: profiles.map((profile: any, index: number) => ({
                id: profile.id,
                title: profile.title,
                imageUrl: profile.image ? profile.image.url : '',
                description: profile.summary,
                completeName: this.stringCamelCase(
                  profile.client.name +
                    ' ' +
                    profile.client.first_surname +
                    ' ' +
                    (profile.client.second_surname || ''),
                ),
                advisor_type: profile?.advisor_type,
                ...(role === 'supplier' ? {
                  recommended: profile?.supplier_approved?.value === 'recommended',
                } : {}),
                tags: profile?.tags,
              })),
              totalPages,
              currentPage,
              totalPosts,
            };
          })
        ));
  }

  async getAdvisorById(id: string) {
    return firstValueFrom(
      this.httpClient.get(`${this.apiUrl}/profiles/${id}`, { headers: this.headers })
      .pipe(
        map((response: any) => {
          return response.data;
        })
      )
    );
  }

  private stringCamelCase(str: string) { 
    // Capitalize first letter of each word, rest of the string in lowercase
    return str.toLowerCase().replace(/(^|\s)(\p{L})/gu, (_, sep, letter) => sep + letter.toUpperCase());
  }


  checkScheduleAvailability(advisorId: string, checkDate: string) {
    const headers = this.authService.getAuthHeaders();
    return firstValueFrom(
      this.httpClient.get(`${this.apiUrl}/profiles/${advisorId}/schedule-availability?check_date=${checkDate}`, { headers })
      .pipe(
        map((response: any) => {
          return response.data;
        })
      )
    );
  }

  createAdvisorCalendarEvent(advisorId: string, event: any) {
    const headers = this.authService.getAuthHeaders();
    const userId = this.authService.currentUser()?.id;
    return firstValueFrom(
      this.httpClient.post(`${this.apiUrl}/profiles/${advisorId}/calendar-event`, { ...event, consumer: userId }, { headers })
      .pipe(
        map((response: any) => {
          return response.data;
        })
      )
    );
  }

  contactSupplier(supplierClientId: string) {
    const apiUrl = this.apiUrl.replace('/public', '/client');
    const headers = this.authService.getAuthHeaders();
    return firstValueFrom(
      this.httpClient.post(`${apiUrl}/suppliers/${supplierClientId}`, {}, { headers })
      .pipe(
        map((response: any) => {
          return response.data;
        })
      )
    );
  }

  // TAGS
  async loadTabsByProfileType(role: string, advisorType?: string) {

    const url = `${this.apiUrl}/profiles/tags?role=${role}${advisorType ? '&advisor_type=' + advisorType : ''}`;

    return firstValueFrom(
      this.httpClient.get(url).pipe(
        map((response: any) => {
          return response.data;
        }),
      ),
    );
  }
}
